import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Select, MenuItem, FormControl, TextField, InputAdornment, CircularProgress, Avatar, Snackbar, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import { getUsers, updateUserRole, updateUserPlan, getPlans, deleteUser } from "../api/adminApi";

const ROLE_COLORS = { admin: "#ef4444", supervisor: "#f59e0b", analyst: "#6366f1", agent: "#10b981", user: "#6b7280" };

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [plans, setPlans] = useState([]);
  const [deleteId, setDeleteId] = useState(null);

  const fetchUsers = () => {
    getUsers({ search }).then((data) => setUsers(data.users || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { getPlans().then(setPlans).catch(() => {}); }, []);

  useEffect(() => { fetchUsers(); }, [search]);

  const handleRoleChange = async (userId, role) => {
    setSaving(userId);
    try {
      await updateUserRole(userId, role);
      setUsers((prev) => prev.map((u) => (u._id === userId ? { ...u, role } : u)));
      setSnack({ open: true, message: "Role updated", severity: "success" });
    } catch { setSnack({ open: true, message: "Failed", severity: "error" }); }
    finally { setSaving(null); }
  };

  const handlePlanChange = async (userId, planId) => {
    setSaving(userId);
    try {
      await updateUserPlan(userId, planId);
      const plan = plans.find(p => p._id === planId);
      setUsers((prev) => prev.map((u) => (u._id === userId ? { ...u, plan: planId, planName: plan?.name, planSlug: plan?.slug } : u)));
      setSnack({ open: true, message: "Plan updated", severity: "success" });
    } catch { setSnack({ open: true, message: "Failed to update plan", severity: "error" }); }
    finally { setSaving(null); }
  };

  const handleDeleteUser = async () => {
    if (!deleteId) return;
    try {
      await deleteUser(deleteId);
      setUsers((prev) => prev.filter((u) => u._id !== deleteId));
      setSnack({ open: true, message: "User deleted", severity: "success" });
    } catch { setSnack({ open: true, message: "Failed to delete user", severity: "error" }); }
    finally { setDeleteId(null); }
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}>
        <Box>
          <Typography fontWeight={800} fontSize={24} color="#1e293b">Users</Typography>
          <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>{users.length} registered users</Typography>
        </Box>
        <TextField size="small" placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#64748b" }} /></InputAdornment>, sx: { bgcolor: "#ffffff", borderRadius: 2, color: "#1e293b", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } } }}
          sx={{ width: 240 }} />
      </Box>
      <Paper className="glass-card" sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>User</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Email</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Plan</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Role</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Joined</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user._id} hover sx={{ "&:hover": { bgcolor: "#f8faff" }, "&:last-child td": { border: 0 } }}>
                  <TableCell sx={{ borderColor: "#e2e8f0" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: user.role === "admin" ? "#ef4444" : "#6366f1", fontSize: 13, fontWeight: 700 }}>
                        {(user.fullName || "U").charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography fontWeight={600} fontSize={14} color="#1e293b" component="a" underline="hover" underlineAlways href={`/users/${user._id}`}>
                        {user.fullName || "Unknown"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderColor: "#e2e8f0" }}><Typography variant="body2" color="#475569">{user.email}</Typography></TableCell>
                  <TableCell sx={{ borderColor: "#e2e8f0" }}>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <Select value={user.plan?._id || user.plan || ""} onChange={(e) => handlePlanChange(user._id, e.target.value)} disabled={saving === user._id}
                        displayEmpty renderValue={(v) => { if (!v) return <Typography color="#64748b" fontSize={13}>No plan</Typography>; const p = plans.find(p => String(p._id) === String(v)); return p?.name || "Unknown"; }}
                        sx={{ color: "#1e293b", borderRadius: 2, fontSize: 13, fontWeight: 600, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" }, "& .MuiSvgIcon-root": { color: "#64748b" } }}>
                        {plans.map((p) => (
                          <MenuItem key={p._id} value={p._id} sx={{ fontSize: 13 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.isActive ? "#10b981" : "#6b7280" }} />
                              {p.name}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ borderColor: "#e2e8f0" }}>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <Select value={user.role} onChange={(e) => handleRoleChange(user._id, e.target.value)} disabled={saving === user._id}
                        sx={{ color: "#1e293b", borderRadius: 2, fontSize: 13, fontWeight: 600, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" }, "& .MuiSvgIcon-root": { color: "#64748b" } }}>
                        {["user", "agent", "analyst", "supervisor", "admin"].map((r) => (
                          <MenuItem key={r} value={r} sx={{ fontSize: 13 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: ROLE_COLORS[r] }} />
                              {r.charAt(0).toUpperCase() + r.slice(1)}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ borderColor: "#e2e8f0" }}>
                    <Typography variant="body2" color="#475569">{new Date(user.createdAt).toLocaleDateString()}</Typography>
                  </TableCell>
                  <TableCell sx={{ borderColor: "#e2e8f0" }}>
                    <Tooltip title="Delete user">
                      <IconButton size="small" onClick={() => setDeleteId(user._id)} sx={{ color: "#ef4444", "&:hover": { bgcolor: "#fef2f2" } }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}
        PaperProps={{ className: "glass-card", sx: { borderRadius: "16px !important", maxWidth: 400 } }}>
        <DialogTitle sx={{ color: "#1e293b", fontWeight: 700 }}>Delete User</DialogTitle>
        <DialogContent>
          <Typography color="#475569">Are you sure? This will also delete all bots owned by this user. This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: "#64748b", textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleDeleteUser} variant="contained" color="error" sx={{ textTransform: "none", borderRadius: 2 }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} sx={{ borderRadius: 2 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
